import { Capacitor } from '@capacitor/core'
// @ts-ignore - plugin provides global import style as well
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth'

// 调试辅助：记录初始化与登录阶段的详细信息，便于定位为何回退到外部浏览器。
// 回退常见根因：SHA-1 未配置、包名不匹配、使用了错误的 clientId、缺失 google-services.json、插件版本/初始化时机问题。

let initialized = false

export function initGoogleAuth() {
  if (initialized) {
    // debug removed: 已经初始化
    return
  }
  const platform = Capacitor.getPlatform()
  if (platform !== 'android') {
    return
  }

  // 按最佳实践：Android 端请求 idToken 需要以 Web Client ID 作为 server audience。
  // 我们同时把 androidClientId / serverClientId 也传入，兼容不同插件版本的字段约定。
  const androidClientId = import.meta.env.VITE_GOOGLE_ANDROID_CLIENT_ID
  const webClientId = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID
  const clientId = webClientId || androidClientId
  // clientId(web优先) web= webClientId android= androidClientId
  if (!clientId) {
  console.error('[googleAuth] 缺少必要的 Google Client ID 环境变量')
    return
  }
  if (!webClientId) {
    // 在 Android 上，Google Sign-In 只有在使用 Web Client ID 作为 server audience 时才会请求 idToken。
    // 没有配置 web client id 将导致 idTokenRequested=false，从而无法从 result.authentication.idToken 取到令牌。
  console.error('[googleAuth] 未检测到 VITE_GOOGLE_WEB_CLIENT_ID，Android 将无法请求 idToken')
  }
  try {
    // forceCodeForRefreshToken 仅在需要离线刷新令牌时启用；这里暂不启用，避免额外授权对话
    const options: any = { clientId, scopes: ['profile', 'email'] }
    // 兼容字段：部分版本使用 serverClientId / androidClientId
    if (webClientId) options.serverClientId = webClientId
    if (androidClientId) options.androidClientId = androidClientId
  // 初始化 GoogleAuth
    GoogleAuth.initialize(options)
    initialized = true
    // 使用 warn 等级以提高在 release 下被 Android WebView 输出的几率
  // GoogleAuth 已初始化
  } catch (e) {
    console.error('[googleAuth] 初始化失败 raw=', e, 'stringified=', safeStringify(e))
  }
}

export async function nativeGoogleSignIn(): Promise<{ idToken: string | null, profile?: any, fallback?: boolean }> {
  initGoogleAuth()
  if (Capacitor.getPlatform() !== 'android') {
    return { idToken: null }
  }
  try {
    const webClientId = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID
    if (!webClientId) {
      // 直接给出明确错误，避免在 UI 里只看到模糊的 "Something went wrong"
      throw new Error('缺少 Web Client ID：请配置环境变量 VITE_GOOGLE_WEB_CLIENT_ID=你的“Web 应用”OAuth 客户端ID（以 .apps.googleusercontent.com 结尾），否则 Android 无法获取 idToken')
    }
    const result: any = await GoogleAuth.signIn()
    // 插件返回结构中若缺少 authentication.idToken，可能已回退到 Web（浏览器）方式
    const idToken = result?.authentication?.idToken || result?.idToken || null
    const fallback = !result?.authentication?.idToken
    if (fallback) {
      // 回退: authentication.idToken 缺失
    }
    return { idToken, profile: result, fallback }
  } catch (e: any) {
    if (e?.error === 'popup_closed' || /cancel/i.test(e?.message || '')) {
      return { idToken: null }
    }
    console.error('[googleAuth] signIn 失败 raw=', e, 'stringified=', safeStringify(e))
    throw e
  }
}

function safeStringify(obj: any) {
  try { return JSON.stringify(obj) } catch { return String(obj) }
}
